import { useState } from 'react';
import { useStore } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { clearSnapshot } from '@/services/save';
import { mkCreature } from '@/domain/creature/factory';
import { randomGenome } from '@/domain/genetics/random';
import { uuid } from '@/infra/util/uuid';

const ASSET_ROOT = '/assets/generated/house';

interface Props {
  onOpenSettings: () => void;
  onOpenBestiary: () => void;
  onOpenBreeding: () => void;
  onOpenLineage: () => void;
  onOpenProfile: () => void;
  onOpenDebug: () => void;
}

export function TopBar({ onOpenSettings, onOpenBestiary, onOpenBreeding, onOpenLineage, onOpenProfile, onOpenDebug }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const handleReset = async (): Promise<void> => {
    if (!confirm(t('settings.reset.confirm'))) return;
    await clearSnapshot();
    const fresh = mkCreature({ genome: randomGenome(), id: uuid(), nowMs: Date.now() });
    useStore.setState({
      creature: fresh,
      npcs: [],
      lineage: { nodes: {}, rootId: null },
      discoveredTypes: [],
    });
  };

  const runOption = (fn: () => void): void => {
    fn();
    setOpen(false);
  };

  return (
    <div data-testid="top-bar" style={bar}>
      <button
        data-testid="btn-settings"
        title={t('topbar.settings')}
        onClick={() => setOpen((v) => !v)}
        style={mainBtn}
        aria-label={t('topbar.settings')}
        aria-expanded={open}
      >
        <img src={`${ASSET_ROOT}/icon-settings.png`} alt="" draggable={false} style={mainIcon} />
      </button>
      {open && (
        <div style={menu}>
          <IconBtn testId="btn-settings-menu" onClick={() => runOption(onOpenSettings)} title={t('topbar.settings')} label={t('topbar.settings')} icon="icon-settings.png" />
          <IconBtn testId="btn-bestiary" onClick={() => runOption(onOpenBestiary)} title={t('topbar.bestiary')} label={t('topbar.bestiary')} icon="icon-bestiary.png" />
          <IconBtn testId="btn-breeding" onClick={() => runOption(onOpenBreeding)} title={t('topbar.breeding')} label={t('topbar.breeding')} icon="icon-breeding.png" />
          <IconBtn testId="btn-lineage" onClick={() => runOption(onOpenLineage)} title={t('topbar.lineage')} label={t('topbar.lineage')} icon="icon-lineage.png" />
          <IconBtn testId="btn-profile" onClick={() => runOption(onOpenProfile)} title={t('topbar.profile')} label={t('topbar.profile')} icon="icon-profile.png" />
          {import.meta.env.DEV && (
            <IconBtn testId="btn-debug" onClick={() => runOption(onOpenDebug)} title="Laboratório de testes" label="Laboratório" icon="icon-heal.png" />
          )}
          <IconBtn testId="btn-reset" onClick={() => void runOption(() => void handleReset())} title={t('topbar.reset')} label="Reset" icon="icon-reset.png" />
        </div>
      )}
    </div>
  );
}

function IconBtn({
  onClick,
  title,
  label,
  testId,
  icon,
}: {
  onClick: () => void;
  title: string;
  label: string;
  testId: string;
  icon: string;
}) {
  return (
    <button data-testid={testId} title={title} onClick={onClick} style={iconBtn} aria-label={title}>
      <span style={iconTile}>
        <img src={`${ASSET_ROOT}/${icon}`} alt="" draggable={false} style={menuIcon} />
      </span>
      <span style={btnLabel}>{label}</span>
    </button>
  );
}

const bar: React.CSSProperties = {
  position: 'fixed',
  top: 10,
  right: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'flex-end',
  zIndex: 100,
};

const mainBtn: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: 12,
  background: 'rgba(8, 10, 22, 0.86)',
  border: '1px solid rgba(255, 226, 188, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 8px 20px rgba(0,0,0,0.22)',
  backdropFilter: 'blur(4px)',
};

const mainIcon: React.CSSProperties = {
  width: 38,
  height: 38,
};

const menu: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  alignItems: 'stretch',
  width: 176,
  padding: 7,
  background: 'rgba(8, 10, 22, 0.82)',
  border: '1px solid rgba(255, 214, 179, 0.38)',
  borderRadius: 12,
  boxShadow: '0 12px 26px rgba(0,0,0,0.24)',
  backdropFilter: 'blur(4px)',
};

const iconBtn: React.CSSProperties = {
  background: 'rgba(12, 12, 24, 0.9)',
  border: '1px solid rgba(255, 226, 188, 0.46)',
  color: '#ffd6b3',
  borderRadius: 8,
  width: '100%',
  height: 50,
  cursor: 'pointer',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 9,
  fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontWeight: 800,
  padding: '4px 10px 4px 8px',
  textTransform: 'uppercase',
  textShadow: '0 1px 1px rgba(0,0,0,0.9)',
  boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
};

const iconTile: React.CSSProperties = {
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: 6,
};

const menuIcon: React.CSSProperties = {
  width: 38,
  height: 38,
  objectFit: 'contain',
  imageRendering: 'auto',
};
const btnLabel: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1,
};
